import React, { useEffect, useRef, useState } from "react";

// Series colors come from the Tailwind theme (--color-series-N), fixed order, never cycled.
export const seriesColor = (index) => `var(--color-series-${index + 1})`;

export const SURFACE = "var(--color-paper)";
export const GRID = "var(--color-gray-200)";
export const AXIS_TEXT = "var(--color-gray-600)";

export function formatNumber(value, decimals = 1) {
	return Number(value).toFixed(decimals).replace("-", "−");
}

// Tracks the rendered width of a container so charts can size to it.
export function useWidth(fallback = 720) {
	const ref = useRef(null);
	const [width, setWidth] = useState(fallback);

	useEffect(() => {
		if (!ref.current) return undefined;
		const observer = new ResizeObserver(([entry]) => {
			setWidth(Math.max(280, Math.floor(entry.contentRect.width)));
		});
		observer.observe(ref.current);
		return () => observer.disconnect();
	}, []);

	return [ref, width];
}

// APA-style figure: number, italic title, optional legend, chart, note, and a table view.
export function Figure({ number, title, legend, note, table, children }) {
	const [showTable, setShowTable] = useState(false);

	return (
		<figure className="my-10">
			<figcaption className="mb-4 font-serif">
				<div className="font-semibold text-ink">Figure {number}</div>
				<div className="italic text-gray-700">{title}</div>
			</figcaption>

			{legend && legend.length > 1 && <Legend items={legend} />}

			{children}

			<div className="mt-3 flex flex-wrap items-start justify-between gap-4">
				{note && (
					<p className="max-w-prose font-serif text-sm text-gray-700">
						<span className="italic">Note.</span> {note}
					</p>
				)}
				{table && (
					<button
						type="button"
						className="shrink-0 cursor-pointer text-sm font-medium text-tide underline underline-offset-2 hover:text-steel"
						aria-expanded={showTable}
						onClick={() => setShowTable(!showTable)}
					>
						{showTable ? "Hide data table" : "Show data table"}
					</button>
				)}
			</div>

			{table && showTable && <DataTable caption={`Data for Figure ${number}`} {...table} />}
		</figure>
	);
}

export function Legend({ items }) {
	return (
		<ul className="mb-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-700">
			{items.map((item) => (
				<li key={item.name} className="flex items-center gap-2">
					{item.shape === "dot" ? (
						<span className="inline-block size-2.5 rounded-full" style={{ backgroundColor: item.color }} />
					) : (
						<span className="inline-block h-0.5 w-4 rounded-full" style={{ backgroundColor: item.color }} />
					)}
					{item.name}
				</li>
			))}
		</ul>
	);
}

export function DataTable({ caption, columns, rows }) {
	return (
		<div className="mt-4 overflow-x-auto">
			<table className="dp-figures min-w-full border-t border-b border-ink text-sm">
				<caption className="sr-only">{caption}</caption>
				<thead>
					<tr className="border-b border-gray-400">
						{columns.map((col) => (
							<th key={col} scope="col" className="px-3 py-2 text-left font-semibold text-ink">{col}</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows.map((row, i) => (
						<tr key={i} className="border-b border-gray-100 last:border-0">
							{row.map((cell, j) => (
								<td key={j} className={`px-3 py-1.5 ${j === 0 ? "text-ink" : "text-right text-gray-700"}`}>{cell}</td>
							))}
						</tr>
					))}
				</tbody>
			</table>
		</div>
	);
}

// Tooltip positioned inside a relative container; flips left near the right edge.
export function Tooltip({ x, y, containerWidth, children }) {
	const flip = x > containerWidth - 220;
	return (
		<div
			role="status"
			className="pointer-events-none absolute z-10 min-w-40 rounded border border-gray-200 bg-white px-3 py-2 text-sm shadow-md"
			style={{
				left: flip ? undefined : x + 14,
				right: flip ? containerWidth - x + 14 : undefined,
				top: Math.max(0, y - 12),
			}}
		>
			{children}
		</div>
	);
}

export function TooltipRow({ color, label, value, shape = "line" }) {
	return (
		<div className="flex items-center gap-2 py-0.5">
			{shape === "dot" ? (
				<span className="inline-block size-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />
			) : (
				<span className="inline-block h-0.5 w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />
			)}
			<span className="dp-figures font-semibold text-ink">{value}</span>
			<span className="text-gray-600">{label}</span>
		</div>
	);
}

export function YGrid({ scale, ticks, width, left, decimals = 0 }) {
	return (
		<g>
			{ticks.map((t) => (
				<g key={t} transform={`translate(0,${scale(t)})`}>
					<line x1={left} x2={left + width} stroke={GRID} strokeWidth="1" shapeRendering="crispEdges" />
					<text x={left - 10} dy="0.32em" textAnchor="end" fontSize="12" fill={AXIS_TEXT} className="dp-figures">
						{formatNumber(t, decimals)}
					</text>
				</g>
			))}
		</g>
	);
}

export function XAxis({ scale, ticks, y, label, format = (t) => t, width, left }) {
	return (
		<g>
			<line x1={left} x2={left + width} y1={y} y2={y} stroke="var(--color-gray-400)" strokeWidth="1" shapeRendering="crispEdges" />
			{ticks.map((t) => (
				<text key={t} x={scale(t)} y={y + 18} textAnchor="middle" fontSize="12" fill={AXIS_TEXT} className="dp-figures">
					{format(t)}
				</text>
			))}
			{label && (
				<text x={left + width / 2} y={y + 40} textAnchor="middle" fontSize="12.5" fill="var(--color-gray-700)">
					{label}
				</text>
			)}
		</g>
	);
}
